import React from 'react';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { Avatar } from '../common/Avatar';
import { TeamBadge } from '../common/TeamBadge';
import { StateBar } from '../floor/StateBar';
import { formatHhMm, formatPercent } from '../../utils/format';
import type { FloorAgentRow, FloorSortKey, SortDir } from './useFloorTime';
interface FloorTableProps {
  rows: FloorAgentRow[];
  sort: {
    key: FloorSortKey;
    dir: SortDir;
  };
  onSort: (key: FloorSortKey) => void;
  onSelect: (id: string) => void;
  header: React.ReactNode;
  footer: React.ReactNode;
}
const columns: {
  key: FloorSortKey;
  label: string;
  align: 'left' | 'right';
}[] = [{
  key: 'agent',
  label: 'Agent',
  align: 'left'
}, {
  key: 'onCall',
  label: 'On Call',
  align: 'right'
}, {
  key: 'waiting',
  label: 'Waiting',
  align: 'right'
}, {
  key: 'ready',
  label: 'Ready',
  align: 'right'
}, {
  key: 'notReady',
  label: 'Not Ready',
  align: 'right'
}, {
  key: 'loggedIn',
  label: 'Total Logged In',
  align: 'right'
}, {
  key: 'focus',
  label: 'Focus %',
  align: 'right'
}];
function focusTone(f: number): string {
  if (f >= 0.8) return 'text-positive';
  if (f >= 0.65) return 'text-ink';
  return 'text-warning';
}
export function FloorTable({
  rows,
  sort,
  onSort,
  onSelect,
  header,
  footer
}: FloorTableProps) {
  return <section aria-label="Agent floor time" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-card" data-id="element-193" data-mp="1mwkpk6fdeu2k">
      <div className="border-b border-line px-6 py-4" data-id="element-194" data-mp="q968q85t0v40">{header}</div>
      <div className="overflow-x-auto" data-id="element-195" data-mp="u9zx12n5sbxf">
        <table className="w-full min-w-[1080px] text-sm" data-id="element-196" data-mp="ub1crkf4dmlh">
          <thead data-id="element-197" data-mp="z702pcg3jco0">
            <tr className="border-b border-line bg-canvas/60 text-xs text-muted" data-id="element-198" data-mp="z6goa5pnn6eq">
              {columns.map(c => {
              const active = sort.key === c.key;
              const Icon = !active ? ChevronsUpDownIcon : sort.dir === 'asc' ? ArrowUpIcon : ArrowDownIcon;
              return <th key={c.key} scope="col" aria-sort={active ? sort.dir === 'asc' ? 'ascending' : 'descending' : 'none'} className={`px-4 py-2 font-medium first:pl-6 ${c.align === 'right' ? 'text-right' : 'text-left'}`} data-id="element-199" data-mp="2fkglh8x48hya">
                    <button type="button" onClick={() => onSort(c.key)} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md py-1 transition-colors duration-150 ease-out hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent ${active ? 'font-semibold text-ink' : ''} ${c.align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-200" data-mp="1ux8wbba551x3">
                      {c.label}
                      <Icon className={`h-3.5 w-3.5 ${active ? 'text-accent' : 'text-subtle'}`} aria-hidden data-id="element-201" data-mp="ogdb5f2ta5gm" />
                    </button>
                  </th>;
            })}
              <th scope="col" className="py-2 pl-4 pr-6 text-left font-medium" data-id="element-202" data-mp="2v55kqicsytr9">
                State split
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-203" data-mp="2zxypv7c6o43v">
            {rows.map(({
            agent,
            states
          }) => {
            const partial = states.loggedIn > 0 && states.unrecorded / states.loggedIn >= 0.1;
            return <tr key={agent.id} tabIndex={0} onClick={() => onSelect(agent.id)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(agent.id);
              }
            }} aria-label={`Open ${agent.name}`} className="cursor-pointer transition-colors duration-150 ease-out hover:bg-canvas/70 focus:outline-none focus-visible:bg-canvas" data-id="element-204" data-mp="3dw6ij326sj98">
                  <td className="py-2.5 pl-6 pr-4" data-id="element-205" data-mp="1xfc2udv9vwyw">
                    <div className="flex items-center gap-3" data-id="element-206" data-mp="2dpl75ubkqrp6">
                      <Avatar name={agent.name} size="sm" data-id="element-207" data-mp="28ycvssqoow9d" />
                      <div className="min-w-0 leading-tight" data-id="element-208" data-mp="28ws215c71hck">
                        <p className="whitespace-nowrap font-semibold text-ink" data-id="element-209" data-mp="2401mcj4awnro">{agent.name}</p>
                        <p className="mt-0.5 flex items-center gap-2 text-xs text-muted" data-id="element-210" data-mp="1z0orpc76ucej">
                          Ext. {agent.extension}
                          <TeamBadge team={agent.team} data-id="element-211" data-mp="2wmw252q6ayyt" />
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-bold tabular-nums text-ink" data-id="element-212" data-mp="2moq10nc6xwv0">{formatHhMm(states.onCall)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink" data-id="element-213" data-mp="2incx4isns3qq">{formatHhMm(states.waiting)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink" data-id="element-214" data-mp="1bbnn601h7lej">{formatHhMm(states.ready)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink" data-id="element-215" data-mp="17au17c7lggoa">{formatHhMm(states.notReady)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink" data-id="element-216" data-mp="3iu9qboum2p1d">
                    {partial && <span className="mr-2 rounded-full bg-silver-soft px-1.5 py-0.5 text-[10px] font-semibold text-silver" title={`${formatHhMm(states.unrecorded)} of login time has no recorded state`} data-id="element-217" data-mp="3rx11dnjkrish">
                        Partial
                      </span>}
                    {formatHhMm(states.loggedIn)}
                  </td>
                  <td className={`px-4 py-2.5 text-right font-semibold tabular-nums ${focusTone(states.focus)}`} data-id="element-218" data-mp="eixqh0bcfaqv">{formatPercent(states.focus)}</td>
                  <td className="py-2.5 pl-4 pr-6" data-id="element-219" data-mp="jeldb92crrt7">
                    <StateBar states={states} size="md" className="w-48" data-id="element-220" data-mp="2gaapx9ujkqfj" />
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
      <div className="border-t border-line px-6 py-3" data-id="element-221" data-mp="3oigg90q7ig8b">{footer}</div>
    </section>;
}